import React, { memo, useCallback, useMemo } from 'react';

import { Box, InputAdornment, Typography } from '@mui/material';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { WORKSHEET_FIELD_FORMAT } from '../../WorksheetFieldsLayout.constants';

import { WORKSHEET_CURRENCY_SYMBOL } from './WorksheetTextField.constants';
import { cleanWorksheetTextInput, formatWorksheetTextValue } from './WorksheetTextField.utils';

/**
 * A worksheet text or number answer, with its hint under the box. An optional format: `currency`
 * shows a $ and accepts an amount only, `phone` is tidied to (234) 567-8901 when the field is left.
 */
const WorksheetTextField = ({
  id,
  name,
  format = null,
  value = '',
  hint = null,
  isRequired = false,
  disabled = false,
  error = null,
  setInForm,
  validateField,
}) => {
  const InputProps = useMemo(
    () =>
      format === WORKSHEET_FIELD_FORMAT.CURRENCY
        ? {
            startAdornment: (
              <InputAdornment position="start">{WORKSHEET_CURRENCY_SYMBOL}</InputAdornment>
            ),
          }
        : null,
    [format],
  );

  const onChange = useCallback(
    e => {
      const nextValue = cleanWorksheetTextInput({ format, value: e.target.value });

      setInForm(id, nextValue);
      validateField(id, nextValue);
    },
    [format, id, setInForm, validateField],
  );

  const onBlur = useCallback(() => {
    const formatted = formatWorksheetTextValue({ format, value });

    if (formatted !== value) {
      setInForm(id, formatted);
    }
  }, [format, id, setInForm, value]);

  // The field's helper text is overlaid (takes no room), so a hint gets its own line below.
  return (
    <Box className="mb-16">
      <TextFieldCDS
        fullWidth
        InputProps={InputProps}
        dataTestId={`worksheet-text.${id}`}
        disabled={disabled}
        error={!!error}
        helperText={error ?? ''}
        label={name}
        name={id}
        required={isRequired}
        value={value ?? ''}
        variant="outlined"
        onBlur={onBlur}
        onChange={onChange}
      />
      {!!hint && !error && (
        <Typography
          className="mt-4"
          color="text.secondary"
          component="div"
          data-testid={`worksheet-text-hint.${id}`}
          variant="regular12"
        >
          {hint}
        </Typography>
      )}
    </Box>
  );
};

WorksheetTextField.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  setInForm: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  format: PropTypes.oneOf(Object.values(WORKSHEET_FIELD_FORMAT)),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hint: PropTypes.string,
  isRequired: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
};

export default memo(WorksheetTextField);
